import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dumbbell, Activity, Zap, Stethoscope, ClipboardCheck, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import WorkloadTypeDetailDialog from "@/components/WorkloadTypeDetailDialog";

const ROW = [
  { key: "strength",          label: "Strength",      Icon: Dumbbell,    color: "#F59E0B" },
  { key: "conditioning",      label: "Conditioning",  Icon: Activity,    color: "#22D3EE" },
  { key: "speed_agility",     label: "Speed/Agility", Icon: Zap,         color: "#A78BFA" },
  { key: "athletic_training", label: "AT Rehab",      Icon: Stethoscope, color: "#34D399" },
];

function Sparkline({ values, color, width = 120, height = 22 }) {
  if (!values || values.length === 0) return null;
  const max = Math.max(1, ...values);
  const n = values.length;
  const stepX = n > 1 ? width / (n - 1) : 0;
  const yFor = (v) => height - 2 - (v / max) * (height - 4);
  // Build the area path (under the line) + the stroke path
  const pts = values.map((v, i) => [i * stepX, yFor(v)]);
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${(n - 1) * stepX},${height} L0,${height} Z`;
  const gid = `g-${color.replace("#", "")}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      {/* last-point dot */}
      <circle cx={pts[n - 1][0]} cy={pts[n - 1][1]} r="1.8" fill={color} />
    </svg>
  );
}

export default function WorkloadSnapshot() {
  const [data, setData] = useState(null);
  const [drillType, setDrillType] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/workload/team-summary?days=7");
        setData(r.data);
      } catch (e) {
        setData({ by_type: {}, total_minutes: 0, total_sessions: 0, pending_count: 0, top_contributors: [] });
      }
    })();
  }, []);

  return (
    <div className="rounded-xl ink-card p-5" data-testid="workload-snapshot">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
          S&amp;C / AT Workload (7d)
        </div>
        <Link
          to="/dashboard/workload"
          className="text-[10px] font-semibold uppercase tracking-[0.2em] flex items-center gap-1"
          style={{ color: "var(--brand-secondary)" }}
          data-testid="workload-snapshot-link"
        >
          Open <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="flex items-baseline gap-2 mt-2">
        <div className="font-display font-black text-5xl tracking-tighter font-mono-num text-white" data-testid="workload-snapshot-total">
          {data?.total_minutes ?? 0}
        </div>
        <div className="text-sm text-white/40">min · {data?.total_sessions ?? 0} sessions</div>
      </div>

      <hr className="my-4 border-[var(--ink-border)]" />

      <div className="space-y-3 text-sm">
        {ROW.map(({ key, label, Icon, color }) => {
          const v = data?.by_type?.[key];
          const min = v?.minutes ?? 0;
          const cnt = v?.sessions ?? 0;
          const series = data?.sparkline?.[key] ?? [];
          return (
            <div key={key} data-testid={`ws-row-${key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white/70">
                  <span
                    className="w-6 h-6 rounded flex items-center justify-center"
                    style={{ backgroundColor: `${color}22`, color }}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  {label}
                </div>
                <div className="font-mono-num text-white">
                  <span className="font-bold">{min}</span>
                  <span className="text-white/40 text-xs ml-1">min · {cnt}</span>
                </div>
              </div>
              <div className="mt-1 pl-8 text-white/30 text-[10px] tracking-[0.18em] uppercase flex items-center justify-between">
                <span>14d trend</span>
                <button
                  type="button"
                  onClick={() => setDrillType(key)}
                  className="rounded transition hover:opacity-90 hover:bg-white/5 px-1 py-0.5 -my-0.5"
                  title={`Click to see daily breakdown for ${label}`}
                  data-testid={`ws-spark-${key}`}
                  aria-label={`Open ${label} 14-day breakdown`}
                >
                  <Sparkline values={series} color={color} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {data?.pending_count > 0 && (
        <>
          <hr className="my-4 border-[var(--ink-border)]" />
          <Link
            to="/dashboard/workload"
            className="flex items-center justify-between rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs hover:bg-amber-500/20"
            data-testid="workload-snapshot-pending"
          >
            <span className="flex items-center gap-2 text-amber-200">
              <ClipboardCheck className="h-3.5 w-3.5" />
              {data.pending_count} self-log{data.pending_count === 1 ? "" : "s"} awaiting verify
            </span>
            <ChevronRight className="h-3 w-3 text-amber-300" />
          </Link>
        </>
      )}

      {data?.top_contributors?.length > 0 && (
        <>
          <hr className="my-4 border-[var(--ink-border)]" />
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">
            Top contributors
          </div>
          <div className="space-y-1.5 text-sm">
            {data.top_contributors.map((c) => (
              <Link
                key={c.player_user_id}
                to={`/dashboard/player/${c.player_user_id}`}
                className="flex items-center justify-between hover:text-white"
                data-testid={`ws-top-${c.player_user_id}`}
              >
                <span className="text-white/70 truncate">{c.player_name || c.player_user_id}</span>
                <span className="font-mono-num text-white/60 text-xs">{c.minutes}m</span>
              </Link>
            ))}
          </div>
        </>
      )}

      <WorkloadTypeDetailDialog
        open={!!drillType}
        onOpenChange={(o) => !o && setDrillType(null)}
        sessionType={drillType}
        days={14}
      />
    </div>
  );
}
